import React, { createContext, useCallback, useContext, useState } from 'react';
import { AppError } from '../api/request';
import UpgradePlanModal from '../components/UpgradePlanModal';

export const PLAN_INSUFFICIENT_CODE = 'ERR_PLAN_INSUFFICIENT';

export interface ErrorState {
  message: string;
  correlationId?: string;
  isUnexpected?: boolean;
}

type ErrorContextValue = {
  error: ErrorState | null;
  showError: (messageOrError: string | Error | AppError | unknown, options?: { correlationId?: string; isUnexpected?: boolean }) => void;
  clearError: () => void;
  upgradeModalOpen: boolean;
  openUpgradeModal: () => void;
  closeUpgradeModal: () => void;
};

const ErrorContext = createContext<ErrorContextValue | null>(null);

function toErrorState(messageOrError: string | Error | AppError | unknown, options?: { correlationId?: string; isUnexpected?: boolean }): ErrorState {
  if (typeof messageOrError === 'string') {
    return {
      message: messageOrError,
      correlationId: options?.correlationId,
      isUnexpected: options?.isUnexpected,
    };
  }
  if (messageOrError instanceof AppError) {
    return {
      message: messageOrError.userMessage,
      correlationId: messageOrError.correlationId,
      isUnexpected: messageOrError.isUnexpected,
    };
  }
  if (messageOrError instanceof Error) {
    return {
      message: messageOrError.message,
      correlationId: options?.correlationId,
      isUnexpected: options?.isUnexpected ?? true,
    };
  }
  return {
    message: String(messageOrError ?? 'Bilinmeyen hata'),
    correlationId: options?.correlationId,
    isUnexpected: options?.isUnexpected ?? true,
  };
}

export function ErrorProvider({ children }: { children: React.ReactNode }) {
  const [error, setError] = useState<ErrorState | null>(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const showError = useCallback((messageOrError: string | Error | AppError | unknown, options?: { correlationId?: string; isUnexpected?: boolean }) => {
    if (messageOrError instanceof AppError && messageOrError.code === PLAN_INSUFFICIENT_CODE) {
      setUpgradeModalOpen(true);
      return;
    }
    setError(toErrorState(messageOrError, options));
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setUpgradeModalOpen(false);
  }, []);

  const openUpgradeModal = useCallback(() => {
    setUpgradeModalOpen(true);
  }, []);

  return (
    <ErrorContext.Provider value={{ error, showError, clearError, upgradeModalOpen, openUpgradeModal, closeUpgradeModal }}>
      {children}
      <UpgradePlanModal open={upgradeModalOpen} onClose={closeUpgradeModal} />
    </ErrorContext.Provider>
  );
}

export function useError() {
  const ctx = useContext(ErrorContext);
  if (!ctx) {
    return {
      error: null,
      showError: (_: string | Error | AppError | unknown, _opts?: { correlationId?: string; isUnexpected?: boolean }) => {},
      clearError: () => {},
      upgradeModalOpen: false,
      openUpgradeModal: () => {},
      closeUpgradeModal: () => {},
    };
  }
  return ctx;
}
